import type { Control } from "react-hook-form";
import { useWatch } from "react-hook-form";

import Switch from "~/components/formElements/Switch/Switch";
import Hint from "~/components/Hint";
import type {
	TimeTrackingFormData,
	TimeTrackingQuotaLock,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import TimeTrackingFormHeader from "~/modules/timeTracking/components/forms/CreateTimeTrackingForm/components/TimeTrackingFormHeader.tsx";
import type { QuotaBookingOption } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	control: Control<TimeTrackingFormData>;
	quotaBookingOptions: QuotaBookingOption[];
	// The value of the entered time. It changes with each input.
	quotaDrawCents: number;
	// Set when the tracking is on a quota that no longer takes its bookings. The section then shows
	// that quota only, with the switch disabled. The form shows the reason at the top.
	quotaLock: TimeTrackingQuotaLock | null;
};

export const QuotaBookingSection = ({ control, quotaBookingOptions, quotaDrawCents, quotaLock }: Props) => {
	const hasMultipleOptions = quotaBookingOptions.length > 1;
	const bookOnQuota = useWatch({ control, name: "bookOnQuota" });
	const budgetIsExceeded = bookOnQuota
		&& quotaBookingOptions.length === 1
		&& quotaDrawCents > quotaBookingOptions[0].availableCents;

	return (
		<>
			<div className="mt-10 flex items-center justify-between">
				<TimeTrackingFormHeader>Abrechnung auf Kontingent</TimeTrackingFormHeader>
				<Switch control={control}
						disabled={!!quotaLock || hasMultipleOptions}
						inputSize="xs"
						name="bookOnQuota" />
			</div>
			<div className="mt-3 max-w-full space-y-1 overflow-hidden text-sm">
				{quotaLock && <div className="flex items-center justify-between gap-x-4">
					<span className="font-medium text-gray-700">{quotaLock.quotaTitle ?? "-"}</span>
					<span className="shrink-0 text-gray-500">Nicht mehr aktiv</span>
				</div>}
				{quotaBookingOptions.map(({ availableCents, quota }) => (
					<div key={quota.id}
						 className="flex items-center justify-between gap-x-4">
						<span className="font-medium text-gray-700">{quota.title}</span>
						<span className="shrink-0 text-gray-500">Verfügbar: {formatCentsToCurrency(availableCents)}</span>
					</div>
				))}
				{budgetIsExceeded && <Hint theme="warning"
										   size="sm">
					<p className="whitespace-pre-wrap break-words">Das verfügbare Budget des Kontingents reicht für
						diese Erfassung nicht aus ({formatCentsToCurrency(quotaDrawCents)}). Bitte die Zeit
						verringern oder die Erfassung ohne Kontingent abrechnen.</p>
				</Hint>}
				{hasMultipleOptions && <Hint theme="warning"
											 size="sm">
					<p className="whitespace-pre-wrap break-words">Dieser Bestellung ist mehr als ein aktives
						Kontingent zugewiesen, daher steht diese Abrechnungsoption nicht zur Verfügung. Dies muss
						durch den Projektverantwortlichen in der Bestellung korrigiert werden.</p>
				</Hint>}
			</div>
		</>
	);
};
